'use client';

import { useState, useMemo, FormEvent, ChangeEvent } from 'react';

type ServiceType = 'recruiting' | 'temp' | 'outsourcing' | 'outstaff';

const layouts: Record<ServiceType, JSX.Element> = {
  recruiting: (
    <div>
      <label className="block text-sm font-medium text-gray-200 mb-1">
        Годовой оклад кандидата, ₽
      </label>
      <input
        type="number"
        name="annual"
        min="120000"
        className="w-full rounded-md px-3 py-2 text-gray-900"
        placeholder="напр., 1 200 000"
        required
      />
    </div>
  ),
  temp: (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Сотрудников, чел.
        </label>
        <input
          type="number"
          name="cnt"
          min="1"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 10"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Ставка, ₽/час
        </label>
        <input
          type="number"
          name="rate"
          min="150"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 350"
          required
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Часов в мес.
        </label>
        <input
          type="number"
          name="hours"
          min="1"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 168"
          required
        />
      </div>
    </div>
  ),
  outsourcing: (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Сотрудников, чел.
        </label>
        <input
          type="number"
          name="cnt"
          min="1"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 8"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Базовая ставка, ₽/мес/сотр.
        </label>
        <input
          type="number"
          name="base"
          min="10000"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 45 000"
          required
        />
      </div>
    </div>
  ),
  outstaff: (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Сотрудников, чел.
        </label>
        <input
          type="number"
          name="cnt"
          min="1"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 12"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Оклад, ₽/мес/сотр.
        </label>
        <input
          type="number"
          name="base"
          min="20000"
          className="w-full rounded-md px-3 py-2 text-gray-900"
          placeholder="напр., 60 000"
          required
        />
      </div>
    </div>
  ),
};

const fmt = (n: number) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
  }).format(n);

const SolutionsEmployers = () => {
  const [serviceType, setServiceType] = useState<ServiceType>('recruiting');
  const [result, setResult] = useState<string | null>(null);

  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | 'warning' | null>(null);

  const [isAgreed, setIsAgreed] = useState(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm({ ...contactForm, [name]: value });
  };

  const handleAgreementChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
    if (e.target.checked) {
      setSubmitStatus(null);
    }
  };

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isAgreed) {
      setSubmitStatus('warning');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/corporate-solutions-requests`;

    try {
      const payload = {
        formName: 'Решения для работодателей',
        ...contactForm,
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: payload }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({ name: '', phone: '', email: '', message: '' });
        setIsAgreed(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    let calculationResult = '';

    switch (serviceType) {
      case 'recruiting': {
        const annual = parseInt(formData.get('annual') as string) || 0;
        const pct = 20;
        const fee = annual * (pct / 100);
        calculationResult = `Комиссия за подбор (~${pct}%): ${fmt(fee)} (при годовом доходе ${fmt(annual)}).`;
        break;
      }
      case 'temp': {
        const cnt = parseInt(formData.get('cnt') as string) || 0;
        const rate = parseInt(formData.get('rate') as string) || 0;
        const hours = parseInt(formData.get('hours') as string) || 0;
        const pct = 15;
        const base = cnt * rate * hours;
        const markup = base * (pct / 100);
        calculationResult = `Месячный бюджет: база ${fmt(base)} + наценка (${pct}%): ${fmt(markup)} = итого ${fmt(base + markup)}.`;
        break;
      }
      case 'outsourcing': {
        const cnt = parseInt(formData.get('cnt') as string) || 0;
        const base = parseInt(formData.get('base') as string) || 0;
        const pct = 25;
        const subtotal = cnt * base;
        const ops = subtotal * (pct / 100);
        calculationResult = `Аутсорсинг: база ${fmt(subtotal)} + операционный пакет (${pct}%): ${fmt(ops)} = ${fmt(subtotal + ops)} в мес.`;
        break;
      }
      case 'outstaff': {
        const cnt = parseInt(formData.get('cnt') as string) || 0;
        const base = parseInt(formData.get('base') as string) || 0;
        const pct = 15;
        const subtotal = cnt * base;
        const admin = subtotal * (pct / 100);
        calculationResult = `Аутстаффинг: оклады ${fmt(subtotal)} + администрирование (${pct}%): ${fmt(admin)} = ${fmt(subtotal + admin)} в мес.`;
        break;
      }
    }
    setResult(calculationResult);
  };

  const renderedFields = useMemo(() => layouts[serviceType], [serviceType]);

  return (
    <div
      className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 antialiased"
      style={{ '--blue': '#003087', '--accent': '#FFA500' } as React.CSSProperties}
    >
      <style>{`
        .btn-accent { background: var(--accent); color: #102a43; }
        .btn-accent:hover { filter: brightness(0.95); }
        .card { background: rgba(255,255,255,0.08); backdrop-filter: blur(8px); }
        .chip { background: rgba(255,255,255,0.14); }
        .simple-card { background: rgba(255,255,255,0.08); padding: 1rem; border-radius: 0.75rem; }
      `}</style>

      <header
        className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{
          background: `
            linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)),
            radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)`,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap items-center gap-3 text-sm">
            <a href="#models" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Модели сотрудничества
            </a>
            <a href="#benefits" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Преимущества
            </a>
            <a href="#case" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Кейс
            </a>
            <a href="#calc" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Калькулятор
            </a>
            <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              FAQ
            </a>
            <a href="#contact" className="chip px-3 py-1 rounded-full hover:bg-white/25">
              Контакты
            </a>
          </nav>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
            Решения для работодателей
          </h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Подберём нужных специалистов, закроем пики загрузки и возьмём часть процессов на себя — быстро,
            прозрачно и с гарантией.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#contact" className="btn-accent px-6 py-3 rounded-xl font-semibold">
              Оставить заявку
            </a>
            <a
              href="#models"
              className="px-4 py-3 rounded-xl font-semibold inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base"
            >
              Смотреть решения
            </a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-10">
        <section id="models" className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Модели сотрудничества
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Подбор персонала</h3>
              <div className="text-3xl font-extrabold mb-2">Комиссия 20%</div>
              <ul className="text-sm space-y-2 text-gray-200">
                <li>• Поиск, воронка, интервью</li>
                <li>• Сопровождение оффера</li>
                <li>• Гарантия замены до 60 дней</li>
              </ul>
              <a
                href="#calc"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Рассчитать стоимость
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Временный персонал</h3>
              <div className="text-3xl font-extrabold mb-2">Ставка + 15%</div>
              <ul className="text-sm space-y-2 text-gray-200">
                <li>• Оперативное усиление штата</li>
                <li>• Замены без доплат</li>
                <li>• Единая отчётность и табели</li>
              </ul>
              <a
                href="#calc"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Считать бюджет
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Аутсорсинг функций</h3>
              <div className="text-3xl font-extrabold mb-2">Фикс + 25%</div>
              <ul className="text-sm space-y-2 text-gray-200">
                <li>• Регламенты и обучение</li>
                <li>• Замещение и резерв</li>
                <li>• Контроль качества и SLA</li>
              </ul>
              <a
                href="#calc"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Запросить КП
              </a>
            </article>

            <article className="card rounded-2xl p-6">
              <h3 className="text-xl font-semibold mb-2">Аутстаффинг</h3>
              <div className="text-3xl font-extrabold mb-2">Оклад + 15%</div>
              <ul className="text-sm space-y-2 text-gray-200">
                <li>• Оформление и кадры на нас</li>
                <li>• Снижение рисков и нагрузки HR</li>
                <li>• Понятная отчётность</li>
              </ul>
              <a
                href="#calc"
                className="mt-5 inline-block btn-accent px-4 py-2 rounded-lg font-semibold"
              >
                Считать бюджет
              </a>
            </article>
          </div>
        </section>

        <section id="benefits" className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Почему это работает
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">Экспертиза</div>
              <p className="text-sm text-gray-200">
                От 7 дней до первых кандидатов. Понимаем специфику отраслей и быстро выходим на релевантные профили.
              </p>
            </div>
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">Прозрачность</div>
              <p className="text-sm text-gray-200">
                Еженедельные отчёты по воронке, SLA в договоре и доступ к метрикам в кабинете.
              </p>
            </div>
            <div className="card rounded-2xl p-6">
              <div className="text-2xl font-bold mb-2">Поддержка</div>
              <p className="text-sm text-gray-200">
                Замены без доплат, сопровождение проверок и адаптации, единая точка контакта.
              </p>
            </div>
          </div>
        </section>

        <section id="case" className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Кейс: склад и логистика
          </h2>
          <div className="card rounded-2xl p-6 grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <p className="text-sm text-gray-200">
                За 6 недель закрыли 48 позиций комплектовщиков и водителей ричтрака для федерального
                дистрибьютора. Обеспечили явку 97% и SLA по замене в 24 часа.
              </p>
              <ul className="text-sm text-gray-200 mt-4 space-y-2">
                <li>• Поток кандидатов — 580 резюме/мес</li>
                <li>• Конверсия из оффера в выход — 83%</li>
                <li>• Табели и KPI — в личном кабинете заказчика</li>
              </ul>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <div className="text-4xl font-extrabold">-22%</div>
              <div className="text-xs text-gray-300">
                снижение совокупной стоимости закрытия
              </div>
              <hr className="my-3 border-white/10" />
              <div className="text-4xl font-extrabold">97%</div>
              <div className="text-xs text-gray-300">
                средняя явка персонала
              </div>
              <hr className="my-3 border-white/10" />
              <div className="text-4xl font-extrabold">24ч</div>
              <div className="text-xs text-gray-300">
                SLA на замену сотрудника
              </div>
            </div>
          </div>
        </section>

        <section id="calc" className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Калькулятор бюджета
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="simple-card">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-gray-200 mb-1">
                    Тип услуги
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value as ServiceType)}
                    className="w-full rounded-md px-3 py-2 text-gray-900"
                  >
                    <option value="recruiting">Подбор персонала — 20%</option>
                    <option value="temp">Временный персонал — 15%</option>
                    <option value="outsourcing">Аутсорсинг — 25%</option>
                    <option value="outstaff">Аутстаффинг — 15%</option>
                  </select>
                </div>
                {renderedFields}
                <button
                  type="submit"
                  className="w-full btn-accent py-2 rounded-md font-semibold"
                >
                  Рассчитать
                </button>
              </form>
            </div>
            <div className="simple-card">
              <h3 className="text-xl font-semibold mb-2">Результат</h3>
              <div id="result" className="text-lg">
                {result || 'Заполните форму, чтобы увидеть расчёт.'}
              </div>
              <p className="text-xs text-gray-300 mt-4">
                * Оценка ориентировочная. Точный расчёт — после короткого брифа.
              </p>
            </div>
          </div>
        </section>

        <section id="faq" className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Частые вопросы
          </h2>
          <div className="space-y-3">
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">
                Сколько длится гарантия?
              </summary>
              <p className="mt-2 text-sm text-gray-200">
                На стандартные роли — до 60 дней, на сложные — до 90 дней, на ретейн — до 120 дней.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">
                Можно ли подписать SLA?
              </summary>
              <p className="mt-2 text-sm text-gray-200">
                Да, фиксируем KPI, сроки закрытия, явку и условия замены в договоре с финансовой ответственностью.
              </p>
            </details>
            <details className="simple-card">
              <summary className="cursor-pointer font-semibold">
                Работаете ли вы по всей стране?
              </summary>
              <p className="mt-2 text-sm text-gray-200">
                Да, у нас есть агенты в регионах и партнёры для локальной поддержки.
              </p>
            </details>
          </div>
        </section>

        <section id="contact" className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Оставить заявку
          </h2>
          <form onSubmit={handleContactSubmit} className="simple-card p-6 grid gap-4 md:grid-cols-2">
            <input
              name="name"
              value={contactForm.name}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Имя"
              required
            />
            <input
              name="phone"
              value={contactForm.phone}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900"
              placeholder="Телефон"
              type="tel"
              required
            />
            <input
              name="email"
              value={contactForm.email}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900 md:col-span-2"
              placeholder="E-mail"
              type="email"
              required
            />
            <textarea
              name="message"
              value={contactForm.message}
              onChange={handleInputChange}
              className="rounded-md px-3 py-2 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Кратко опишите задачу"
              required
            ></textarea>

            <div className="flex items-center md:col-span-2">
              <input
                type="checkbox"
                id="privacyAgreementContact"
                checked={isAgreed}
                onChange={handleAgreementChange}
                className="w-4 h-4 text-[#FFA500] bg-gray-100 border-gray-300 rounded focus:ring-[#FFA500] shrink-0"
              />
              <label htmlFor="privacyAgreementContact" className="ml-2 text-xs font-light text-gray-200 cursor-pointer">
                Нажимая "Отправить", вы соглашаетесь с&nbsp;
                <a href="/privacy" className="text-[#FFA500] hover:underline font-normal" target="_blank" rel="noopener noreferrer">
                  Политикой обработки персональных данных
                </a>
                .
              </label>
            </div>

            <button
              type="submit"
              className="btn-accent rounded-md py-2 font-semibold md:col-span-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>

            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                Ваша заявка успешно отправлена!
              </div>
            )}
            {submitStatus === 'warning' && (
              <div className="md:col-span-2 text-center text-sm text-yellow-400">
                Пожалуйста, примите Политику обработки персональных данных.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                Ошибка при отправке. Пожалуйста, попробуйте снова.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
};

export default SolutionsEmployers;